import React from 'react';
import { Field } from 'react-final-form';
import { required, minLength, composeValidators, validEmail } from '@/components/common/Forms/Validators';
import Tooltip from '@/components/common/Tooltip';
import AllowMultipleSessionsField from '@/components/admin/FormFields/AllowMultipleSessionsField';
import Select from '@/components/common/ReactSelect5';
import PasswordRequirements from '@/components/shared/PasswordRequirements';
import styles from '../StaffForm.module.scss';

const FirstNameField = () => (
  <Field
    name="first_name"
    validate={required}
  >
    {({ input, meta }) => (
      <div className={styles.row}>
        <div className={styles.fieldContainer}>
          <label className={styles.label} htmlFor="staff-first-name">
            First Name
            <span className={styles.red}>*</span>
          </label>

          <input
            className={styles.textInput}
            id="staff-first-name"
            {...input}
          />
        </div>

        {meta.error && meta.touched && <div className={styles.fieldError}>{meta.error}</div>}
      </div>
    )}
  </Field>
);

const LastNameField = () => (
  <Field
    name="last_name"
    validate={required}
  >
    {({ input, meta }) => (
      <div className={styles.row}>
        <div className={styles.fieldContainer}>
          <label className={styles.label} htmlFor="staff-last-name">
            Last Name
            <span className={styles.red}>*</span>
          </label>

          <input
            className={styles.textInput}
            id="staff-last-name"
            {...input}
          />
        </div>

        {meta.error && meta.touched && <div className={styles.fieldError}>{meta.error}</div>}
      </div>
    )}
  </Field>
);

const EmailField = () => (
  <Field
    name="email"
    validate={composeValidators(required, validEmail)}
  >
    {({ input, meta }) => (
      <div className={styles.row}>
        <div className={styles.fieldContainer}>
          <label className={styles.label} htmlFor="staff-email">
            Email
            <span className={styles.red}>*</span>
          </label>

          <input
            className={styles.textInput}
            id="staff-email"
            type="email"
            {...input}
          />
        </div>

        {meta.error && meta.touched && <div className={styles.fieldError}>{meta.error}</div>}
      </div>
    )}
  </Field>
);

const UsernameField = () => (
  <div className={styles.row}>
    <div className={styles.fieldContainer}>
      <label className={styles.label} htmlFor="staff-username">
        Username
      </label>
      <Field
        className={styles.textInput}
        component="input"
        id="staff-username"
        name="username"
        parse={val => (val === undefined ? '' : val)}
      />
    </div>
  </div>
);

interface PasswordFieldProps {
  hasStrictPasswordRequirement?: boolean;
  isSendPasswordSetupEmailChecked?: boolean;
  newRecord?: boolean;
  userIsSysadmin: boolean;
}

type PasswordLabel = string | JSX.Element;

const PasswordField = ({
  hasStrictPasswordRequirement,
  isSendPasswordSetupEmailChecked,
  newRecord,
  userIsSysadmin,
}: PasswordFieldProps) => {
  let labelText: PasswordLabel = 'New Password';
  if (newRecord) {
    labelText = (
      <span>
        Password
        <span className={styles.red}>*</span>
      </span>
    );
  }

  const sendPasswordSetupEmail = (
    <div className={`${styles.fieldContainer} tw-mt-2`}>
      <div className={styles.label} />
      <Field
        component="input"
        id="send-password-setup-email"
        name="send_password_setup_email"
        type="checkbox"
      />
      <label className={styles.checkboxLabel} htmlFor="send-password-setup-email">
        {`Send ${newRecord ? 'set up password' : 'password reset'} email`}
      </label>
    </div>
  );

  const requirePasswordReset = (
    <div className={`${styles.fieldContainer} tw-mt-2`}>
      <div className={styles.label} />
      <Field
        component="input"
        id="require-password-reset"
        name="require_password_reset"
        type="checkbox"
      />
      <label className={styles.checkboxLabel} htmlFor="require-password-reset">
        Require a password change at the next sign-in
      </label>
    </div>
  );

  const validate = (value, allValues) => {
    if (allValues.send_password_setup_email) return undefined;

    if (hasStrictPasswordRequirement) {
      const validator = newRecord ? required : undefined;
      return validator ? validator(value) : undefined;
    }

    const validator = newRecord ? composeValidators(minLength(6), required) : minLength(6);
    return validator(value);
  };

  return (
    <Field
      name="password"
      validate={validate}
    >
      {({ input, meta }) => (
        <div className={styles.row}>
          <div className={styles.fieldContainer}>
            <label className={styles.label} htmlFor="staff-password">
              {labelText}
            </label>
            <input
              autoComplete="new-password"
              className={styles.textInput}
              disabled={isSendPasswordSetupEmailChecked}
              id="staff-password"
              type="password"
              {...input}
            />
          </div>

          {hasStrictPasswordRequirement && !isSendPasswordSetupEmailChecked && (
            <PasswordRequirements
              maxWidth={330}
              password={input.value || ''}
              spacerWidth={140}
            />
          )}

          {userIsSysadmin && sendPasswordSetupEmail}
          {userIsSysadmin && requirePasswordReset}

          {
            // `hasStrictPasswordRequirement` displays its errors via the PasswordRequirements component
            meta.error && meta.touched && !hasStrictPasswordRequirement
            && <div className={styles.fieldError}>{meta.error}</div>
          }
        </div>
      )}
    </Field>
  );
};

const ExcludeFromRosteringTooltipText = () => {
  const url = 'https://www.teachtci.com/exclude-teacher';

  return (
    <span>
      Excluding a teacher will leave the teacher&apos;s classes
      (including student rosters) unmodified during a roster sync. See&nbsp;
      <a href={url} rel="noreferrer noopener" target="_blank">
        How to exclude a teacher from auto-rostering
      </a>
      &nbsp;for more details.
    </span>
  );
};

const ExcludeFromRosteringField = () => (
  <div className={styles.row}>
    <div className={styles.fieldContainer}>
      <label className={styles.label} htmlFor="staff-exclude-from-rostering">
        Exclude from auto-rostering&nbsp;
        <Tooltip content={<ExcludeFromRosteringTooltipText />} theme="white">
          <div className="inline_block">
            <i aria-hidden="true" className="fa fa-question-circle" />
            <span className="sr-only">More info</span>
          </div>
        </Tooltip>
      </label>

      <Field
        component="input"
        id="staff-exclude-from-rostering"
        name="exclude_from_rostering"
        parse={val => (val === true ? 'true' : false)}
        type="checkbox"
      />
    </div>
  </div>
);

const AccessTypeField = () => (
  <div className={styles.row}>
    <div className={styles.fieldContainer}>
      <span className={styles.label}>Access Type</span>
      <label className={styles.radioLabel}>
        <Field
          className={styles.radioInput}
          component="input"
          name="type"
          type="radio"
          value="Sysadmin"
        />
        {' '}
        Full Access
      </label>

      <label className={styles.radioLabel}>
        <Field
          className={styles.radioInput}
          component="input"
          name="type"
          type="radio"
          value="SupportManager"
        />
        {' '}
        Support Only
      </label>

      <label className={styles.radioLabel}>
        <Field
          className={styles.radioInput}
          component="input"
          name="type"
          type="radio"
          value="ContentManager"
        />
        {' '}
        Content Only
      </label>
    </div>
  </div>
);

const RoleField = ({ form, isTeacher }) => {
  const includeInContactPopup = (
    <div className={`${styles.fieldContainer} tw-mt-2`}>
      <div className={styles.label} />
      <Field
        component="input"
        id="staff-include-as-contact-admin"
        name="include_as_contact_admin"
        type="checkbox"
      />
      <label className="tw-ml-1" htmlFor="staff-include-as-contact-admin">
        Include in Contact Admin popup&nbsp;
        <Tooltip
          content="Unchecking this field will hide this admin as a contact option for teachers."
          interactive={false}
          theme="white"
        >
          <div className="inline_block">
            <i aria-hidden="true" className="fa fa-question-circle" />
            <span className="sr-only">More info</span>
          </div>
        </Tooltip>
      </label>
    </div>
  );

  return (
    <div className={styles.row}>
      <div className={styles.fieldContainer}>
        <span className={styles.label}>Role</span>

        <label className={styles.radioLabel}>
          <Field
            className={styles.radioInput}
            component="input"
            name="type"
            type="radio"
            value="Teacher"
            onClick={form.mutators.handleSelectRoleField}
          />{' '}
          Teacher
        </label>

        <label className={styles.radioLabel}>
          <Field
            className={styles.radioInput}
            component="input"
            name="type"
            type="radio"
            value="Coordinator"
            onClick={form.mutators.handleSelectRoleField}
          />{' '}
          Coordinator
        </label>

        <label className={styles.radioLabel}>
          <Field
            className={styles.radioInput}
            component="input"
            name="type"
            type="radio"
            value="Admin"
            onClick={form.mutators.handleSelectRoleField}
          />{' '}
          Admin
        </label>
      </div>
      { !isTeacher && includeInContactPopup }
    </div>
  );
};

const UIDField = () => (
  <div className={styles.row}>
    <div className={styles.fieldContainer}>
      <label className={styles.label} htmlFor="staff-uid">
        UID
      </label>
      <Field
        className={styles.textInput}
        component="input"
        id="staff-uid"
        name="uid"
      />
    </div>
  </div>
);

const ReactSelectAdapter = ({ input, ...rest }) => (
  <Select
    {...input}
    {...rest}
  />
);

const ContactType = ({ contactTypeOptions }) => (
  <div className={styles.row}>
    <div className={styles.fieldContainer}>
      <label className={styles.label} htmlFor="contact_types">
        Contact Type
        {' '}
        <Tooltip
          content="Designate this staffer as a point of contact for tech integration or curriculum-related queries."
          interactive={false}
          theme="white"
        >
          <div className="inline_block">
            <i aria-hidden="true" className="fa fa-question-circle" />
            <span className="sr-only">More info</span>
          </div>
        </Tooltip>
      </label>
      <Field
        isMulti
        className="tw-w-[330px]"
        component={ReactSelectAdapter}
        id="contact_types"
        name="contact_types"
        options={contactTypeOptions}
        placeholder="Select Contact Type"
      />
    </div>
  </div>
);

const Fields = {
  AccessTypeField,
  AllowMultipleSessionsField,
  ContactType,
  EmailField,
  ExcludeFromRosteringField,
  FirstNameField,
  LastNameField,
  PasswordField,
  RoleField,
  UIDField,
  UsernameField,
};

export default Fields;
